/**
 * InlineOptionPicker
 *
 * Canonical pill selector for dialogue forms (Fresh/Frozen, Partner/Donor,
 * IVF/ICSI, …). One of N by default; pass an array `value` where several can
 * be chosen (times of day). Selected pill takes the deep-brown selection
 * tint; each option may carry a leading icon and a small detail line. For
 * longer lists pass `wrap` so pills flow onto multiple rows.
 */

import { Pressable, Text, View } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type InlineOption = {
  id: string;
  label: string;
  detail?: string;
  icon?: LucideIcon;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  testID?: string;
  /** Shown, but not choosable: an option the current selection rules out. */
  disabled?: boolean;
};

const SELECTION_ACCENT = colors.deepBrown;

export function InlineOptionPicker({
  options,
  value,
  onChange,
  wrap = false,
  columns = 3,
}: {
  options: InlineOption[];
  /**
   * The chosen option's id. Pass an array of ids where several options can
   * be chosen at once: `onChange` then reports the tapped option, and the
   * caller adds or removes it.
   */
  value: string | readonly string[];
  onChange: (value: string) => void;
  wrap?: boolean;
  columns?: 2 | 3;
}) {
  const capturePress = usePressCapture("InlineOptionPicker");
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: wrap ? "wrap" : "nowrap",
        gap: 8,
      }}
    >
      {options.map((option) => {
        const selected =
          typeof value === "string"
            ? option.id === value
            : value.includes(option.id);
        const OptionIcon = option.icon;
        return (
          <Pressable
            key={option.id}
            disabled={option.disabled}
            onPress={() => {
              capturePress(
                pressLabel(option.label),
                option.action ?? option.id,
              );
              onChange(option.id);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected, disabled: option.disabled }}
            aria-selected={selected}
            testID={option.testID}
            style={{
              opacity: option.disabled ? 0.35 : 1,
              flex: wrap ? undefined : 1,
              flexGrow: wrap ? 1 : undefined,
              flexBasis: wrap ? (columns === 2 ? "46%" : "30%") : undefined,
              alignItems: "center",
              justifyContent: "center",
              // An icon sits beside the label, so it adds no height: a pill
              // with an icon matches one without.
              minHeight: option.detail ? 58 : 42,
              minWidth: wrap ? 104 : undefined,
              paddingHorizontal: 8,
              paddingVertical: 8,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: selected
                ? `${SELECTION_ACCENT}30`
                : "rgba(0,0,0,0.06)",
              backgroundColor: selected
                ? `${SELECTION_ACCENT}18`
                : "transparent",
            }}
          >
            <View style={{ alignItems: "center", gap: 2, maxWidth: "100%" }}>
              {/* A long label wraps inside its tile, beside its icon, instead
                  of running past the tile's edge. */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 5,
                  maxWidth: "100%",
                }}
              >
                {OptionIcon ? (
                  <OptionIcon
                    size={14}
                    color={selected ? SELECTION_ACCENT : colors.textMid}
                    strokeWidth={1.8}
                  />
                ) : null}
                <Text
                  style={{
                    flexShrink: 1,
                    fontSize: 12,
                    fontWeight: selected ? "600" : "400",
                    color: selected ? SELECTION_ACCENT : colors.textMid,
                    textAlign: "center",
                  }}
                >
                  {option.label}
                </Text>
              </View>
              {option.detail ? (
                <Text
                  style={{
                    fontSize: 10,
                    color: selected ? SELECTION_ACCENT : colors.textFaint,
                    textAlign: "center",
                  }}
                >
                  {option.detail}
                </Text>
              ) : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

export default InlineOptionPicker;
